import { getStationaryBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingStepSkeleton } from '@components/BookingFlow/Common/BookingStepSkeleton';
import { BookingStepUnavailablePage } from '@components/BookingFlow/Common/BookingStepUnavailablePage';
import { ExtraServiceCards } from '@components/BookingFlow/Stationary/ExtraServiceCards';
import { OpenOrdersProducts } from '@components/BookingFlow/Stationary/OpenOrdersProducts';
import { ServiceCard } from '@components/BookingFlow/Stationary/ServiceCard';
import { StationaryBookingScreen } from '@components/BookingFlow/Stationary/StationaryBookingScreen';
import { TireHotelAtOtherDepartmentPrompt } from '@components/BookingFlow/Stationary/TireHotelAtOtherDepartmentPrompt';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AnimatedList, NoddiInfoBanner, NoddiLink } from 'noddi-ui-web';
import { customerRoutes, resolveIncludedOrderLines } from 'noddi-util';
import { PriceListContextBanner } from 'noddi-web';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStationaryBookableServices } from '../../../../hooks/useStationaryBookableServices';
import {
  useServiceCenter,
  useStationaryAddonServices,
  useStationaryBookingActions,
  useStationaryBookingCar,
  useStationaryPrimaryService
} from '../../../../stores/StationaryBookingStore';
import type { StationaryService } from '../../../../types/stationaryBooking';
import { getCommonTranslations } from '../../../../utils/commonTranslations';

export const StationaryBookingServices = () => {
  const navigate = useNavigate();
  const { setPrimaryService, setAddonServices } = useStationaryBookingActions();
  const primarySelectedService = useStationaryPrimaryService();
  const addonServices = useStationaryAddonServices();
  const serviceCenter = useServiceCenter();
  const selectedCar = useStationaryBookingCar();

  const {
    nonEditableServices,
    selectableServices,
    enforcedPrimary,
    error: bookingContextError,
    isLoading: isBookingContextLoading,
    services: servicesData,
    openOrder,
    priceListName,
    discountPercent
  } = useStationaryBookableServices();

  // single-primary: policy.enforcedPrimary (partner multi-line uses all-non-editable).
  useEffect(() => {
    if (enforcedPrimary && primarySelectedService?.salesItemId !== enforcedPrimary.salesItemId) {
      setPrimaryService(enforcedPrimary);
    }
  }, [enforcedPrimary, primarySelectedService?.salesItemId, setPrimaryService]);

  // The catalog query needs both, so without them the step would sit pending
  // forever (deep link / cleared draft). Same handling as the mobile flow:
  // clear the draft and restart, and log which prerequisite was missing.
  if (!serviceCenter || !selectedCar) {
    return <BookingStepUnavailablePage missing={!serviceCenter ? 'serviceCenter' : 'car'} />;
  }

  function handleServiceClick(service: StationaryService) {
    if (!service.isCustomerEditable) {
      return;
    }
    const isAlreadySelected = primarySelectedService?.salesItemId === service.salesItemId;
    if (isAlreadySelected) {
      setPrimaryService(null);
      setAddonServices([]);
    } else {
      setPrimaryService(service);
    }
  }

  function navigateToNextStep() {
    const isIncomingTireSeasonRequired = [primarySelectedService, ...addonServices].some(
      (service) => service?.isIncomingTireSeasonRequired
    );
    navigate(
      isIncomingTireSeasonRequired
        ? customerRoutes.newStationaryBookingTireSeason.getPath()
        : customerRoutes.newStationaryBookingTimeWindow.getPath()
    );
  }

  const priceListContextBanner = getCommonTranslations().priceListContextBanner;
  const hasSelectable = selectableServices.length > 0;
  const includedOrderLines = resolveIncludedOrderLines({ fixedServices: nonEditableServices, openOrder });
  // Fixed inclusions on screen — the selection needs its own heading to stay distinguishable.
  const hasIncludedItems = includedOrderLines.length > 0;
  const isLoadingServices = isBookingContextLoading || !servicesData;
  // Catalog answered, but nothing bookable for this car at this service center.
  const hasNoServices =
    !isLoadingServices && !bookingContextError && !hasSelectable && nonEditableServices.length === 0;

  return (
    <StationaryBookingScreen
      progress={getStationaryBookingProgress('services')}
      onNextClicked={navigateToNextStep}
      title={t`What can we help you with?`}
      disableNextButton={!primarySelectedService}
      validationVariant='serviceSelectionRequired'
    >
      <TireHotelAtOtherDepartmentPrompt />
      {bookingContextError ? (
        <ApiErrorMessageWithTrans error={bookingContextError} />
      ) : isLoadingServices ? (
        <BookingStepSkeleton rowClassName='h-28' />
      ) : hasNoServices ? (
        <NoServicesAvailable />
      ) : (
        <AnimatedList className='flex flex-col gap-5' delayBetweenItems={100}>
          {/* First: what the car already has coming, before the choice on this step. */}
          <OpenOrdersProducts lines={includedOrderLines} contextPriceListName={priceListName} />
          {priceListName && (
            <PriceListContextBanner
              priceListName={priceListName}
              discountPercent={discountPercent}
              translations={priceListContextBanner}
            />
          )}
          {hasSelectable && (
            <div className='flex flex-col gap-4'>
              {hasIncludedItems && <p className='text-lg font-semibold'>{t`Choose service`}</p>}
              {selectableServices.map((service) => {
                const isAlreadySelected = primarySelectedService?.salesItemId === service.salesItemId;

                return (
                  <ServiceCard
                    key={service.salesItemId}
                    service={service}
                    isAlreadySelected={!!isAlreadySelected}
                    handleServiceClick={handleServiceClick}
                    contextPriceListName={priceListName}
                  />
                );
              })}
            </div>
          )}

          {primarySelectedService && <ExtraServiceCards primaryService={primarySelectedService} />}
        </AnimatedList>
      )}
    </StationaryBookingScreen>
  );
};

/** Catalog returned nothing bookable for this car here - offer a way out. */
const NoServicesAvailable = () => (
  <NoddiInfoBanner
    variant='info'
    title={t`No services available for this car here`}
    texts={[{ value: t`Try another service center, or choose home delivery instead.` }]}
    actionNode={
      <NoddiLink to={customerRoutes.newBookingChoice.getPath()} size='small' variant='outline'>
        <Trans>Change service center</Trans>
      </NoddiLink>
    }
  />
);
